import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  GetUploadedEventsAPI,
  type StudentEvent,
} from "@/services/api/user/events";
import { GetUserSocietiesAPI } from "@/services/api/society/getUserSocieties";
import EventFormWizard from "@/components/student/EventFormWizard";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";

const LOOKUP_PAGES = 5;

/**
 * Organizer event editing — the web port of the RN `EditEventScreen`.
 * Reuses the create wizard in edit mode, pre-filled
 * from the stored event. The event normally arrives through the router state
 * from the "My events" list; a direct URL open is resolved by scanning the
 * organizer's uploaded events.
 */
const StudentEditEventPage: React.FC = () => {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const stateEvent = (location.state as { event?: StudentEvent } | null)?.event;

  const [event, setEvent] = useState<StudentEvent | null>(stateEvent ?? null);
  const [loading, setLoading] = useState(!stateEvent);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!stateEvent) return;
    setEvent(stateEvent);
    setNotFound(false);
    setLoading(false);
  }, [stateEvent]);

  useEffect(() => {
    if (stateEvent || !eventId) return;
    let cancelled = false;
    const lookup = async () => {
      setLoading(true);
      const societies = await GetUserSocietiesAPI();
      const societyIds = societies.map((society) => society.id);
      for (let page = 1; page <= LOOKUP_PAGES; page += 1) {
        const resp = await GetUploadedEventsAPI(societyIds, page);
        const result = resp?.result;
        if (!Array.isArray(result)) break;
        const match = result.find((item) => item.details.id === eventId);
        if (match) {
          if (cancelled) return;
          setEvent(match);
          setLoading(false);
          return;
        }
        if (result.length < 10) break;
      }
      if (cancelled) return;
      setLoading(false);
      setNotFound(true);
    };
    lookup();
    return () => {
      cancelled = true;
    };
  }, [stateEvent, eventId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6">
        <AdminListSkeleton count={3} />
      </div>
    );
  }

  if (!event || notFound) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="Ionicons"
            iconName="alert-circle-outline"
            title={t("Event not found")}
            message={t("This event is no longer available.")}
            actionLabel={t("Back to Home")}
            onAction={() => navigate("/student/home")}
          />
        </div>
      </div>
    );
  }

  return <EventFormWizard mode="edit" event={event} />;
};

export default StudentEditEventPage;
